import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { User, Mail, Phone, LogOut, ShieldCheck, Lock } from 'lucide-react';
import { toast } from 'sonner';
import CustomerHeader from '../../components/CustomerHeader';
import EditNameField from './Profile/EditNameField';
import EditPhoneFlow from './Profile/EditPhoneFlow';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, isGuest, logout, loading: authLoading } = useAuth();

  React.useEffect(() => {
    // Wait for AuthContext to hydrate from localStorage on F5 — otherwise
    // `user` is null on first render and we'd wrongly bounce to "/".
    if (authLoading) return;
    if (!user) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [user, authLoading, navigate]);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    // Use setTimeout to avoid React concurrent rendering error
    setTimeout(() => {
      navigate('/');
    }, 0);
  };

  // Render nothing while AuthContext is still hydrating or after a logout.
  // The effect above takes care of redirecting once hydration completes
  // and confirms no user is present.
  if (authLoading || !user) {
    return null;
  }

  return (
    <div className="mobile-container min-h-screen pb-bottom-nav" data-testid="profile-page">
      <CustomerHeader title="Profile" onBack={() => navigate('/home')} />

      <div className="p-6 space-y-6">
        {/* Profile Info */}
        <Card className="p-6 glass-card">
          <div className="flex items-center justify-center mb-6">
            <div className="w-20 h-20 bg-accent rounded-full flex items-center justify-center">
              <User className="w-10 h-10 text-accent-foreground" />
            </div>
          </div>

          <div className="space-y-5">
            {!isGuest && user && (
              <>
                <EditNameField />

                <div>
                  <p className="text-xs uppercase font-bold text-muted-foreground mb-1">Email</p>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Mail className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      <p className="font-mono text-sm truncate" data-testid="profile-email-value">
                        {user?.email}
                      </p>
                    </div>
                    <span
                      className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-full flex-shrink-0"
                      data-testid="profile-email-verified-badge"
                    >
                      <ShieldCheck className="w-3.5 h-3.5" />
                      Verified
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Email cannot be changed
                  </p>
                </div>

                <EditPhoneFlow />
              </>
            )}
            {isGuest && user && (
              <div>
                <p className="text-xs uppercase font-bold text-muted-foreground mb-1">Phone (Guest)</p>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground" />
                  <p className="font-mono text-sm">{user?.phone}</p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Account Type */}
        <Card className="p-4 glass-card bg-muted/30">
          <p className="text-sm text-center text-muted-foreground">
            {isGuest ? 'Guest Account' : 'Registered Account'}
          </p>
        </Card>

        {/* Logout */}
        <Button
          onClick={handleLogout}
          data-testid="logout-button"
          variant="destructive"
          className="w-full h-14 rounded-full"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Logout
        </Button>
      </div>
    </div>
  );
}
